import { useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PencilSimple } from "@phosphor-icons/react";

const HALVES = [
  { key: "first", label: "First Half" },
  { key: "second", label: "Second Half" },
];

const CHOICES = [
  { key: "keep", label: "Leave unchanged" },
  { key: "present", label: "Present" },
  { key: "absent", label: "Absent" },
];

/* Admin override dialog: mark any staff member present/absent for a given date. */
export function ManualAttendanceDialog({ open, onOpenChange, users, initial, onSaved }) {
  const [form, setForm] = useState({ user_id: "", date: "", first_half: "keep", second_half: "keep", note: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().slice(0, 10);
    setForm({
      user_id: initial?.user_id || users[0]?.id || "",
      date: initial?.date || today,
      first_half: initial?.first_half || "keep",
      second_half: initial?.second_half || "keep",
      note: "",
    });
  }, [open, initial, users]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.date) { toast.error("Staff and date are required"); return; }
    if (form.first_half === "keep" && form.second_half === "keep") { toast.error("Pick a status for at least one half"); return; }
    setSaving(true);
    try {
      await http.post("/attendance/manual", {
        user_id: form.user_id, date: form.date, note: form.note,
        first_half: form.first_half === "keep" ? null : form.first_half,
        second_half: form.second_half === "keep" ? null : form.second_half,
      });
      toast.success("Attendance updated");
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const triggerCls = "rounded-none border-zinc-400";
  const inputCls = "rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-lg" data-testid="manual-attendance-dialog">
        <DialogHeader>
          <div className="label-overline text-orange-600 mb-1 flex items-center gap-1.5"><PencilSimple size={12} weight="bold" /> Admin override</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">Manual Attendance</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="manual-attendance-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-overline block mb-1 text-zinc-700">Staff *</label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="manual-user" className={triggerCls}><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent className="rounded-none max-h-72">
                  {users.map((u) => <SelectItem key={u.id} value={u.id} className="rounded-none">{u.name} · {u.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-overline block mb-1 text-zinc-700">Date *</label>
              <Input type="date" value={form.date} max={new Date().toISOString().slice(0, 10)} data-testid="manual-date"
                     onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            </div>
            {HALVES.map(({ key, label }) => (
              <div key={key}>
                <label className="label-overline block mb-1 text-zinc-700">{label}</label>
                <Select value={form[`${key}_half`]} onValueChange={(v) => setForm({ ...form, [`${key}_half`]: v })}>
                  <SelectTrigger data-testid={`manual-${key}`} className={triggerCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {CHOICES.map((c) => <SelectItem key={c.key} value={c.key} className="rounded-none">{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div>
            <label className="label-overline block mb-1 text-zinc-700">Note</label>
            <Textarea rows={2} value={form.note} placeholder="e.g. Was on client site, confirmed by phone" data-testid="manual-note"
                      onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="manual-submit"
                    className="rounded-none bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs">
              {saving ? "Saving…" : "Save Attendance"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
